import React, { useState, useEffect } from 'react';
import { showSuccess, showError } from '../utils/alertas';

const IS_PROD = true;
const API_BASE_URL = IS_PROD ? 'https://sigoapi.jhe-etes.com.br/api' : 'http://localhost:3001/api';

const NovoContratoModal = ({ isOpen, onClose, empreendimentoId, onSaveSuccess }) => {
    const [isLoading, setIsLoading] = useState(false);
    
    const [formData, setFormData] = useState({
        codigo_contrato: '',
        nome: '',
        status_id: '2', // Default 2: Em Obras
        valorContratoCadastroETE: '',
        objetoResumidoETE: ''
    });

    const [statusObras, setStatusObras] = useState([]);
    const [faltantes, setFaltantes] = useState([]);

    // Fetch status_obras
    useEffect(() => {
        if (isOpen) {
            fetch(`${API_BASE_URL}/status_obras`)
                .then(res => res.json())
                .then(data => setStatusObras(data))
                .catch(err => console.error("Erro ao carregar status de obras:", err));
        }
    }, [isOpen]);

    // Limpar form ao fechar/abrir
    useEffect(() => {
        if (!isOpen) {
            setFormData({
                codigo_contrato: '',
                nome: '',
                status_id: '2',
                valorContratoCadastroETE: '',
                objetoResumidoETE: ''
            });
            setFaltantes([]);
        }
    }, [isOpen]);

    const handleInputChange = (e) => {
        const { name, value } = e.target;
        setFormData(prev => ({ ...prev, [name]: value }));
        
        if (faltantes.includes(name) && value.trim() !== '') {
            setFaltantes(prev => prev.filter(f => f !== name));
        }
    };

    const handleMoneyChange = (e) => {
        const { name, value } = e.target;
        // Permite apenas nmeros, vrgula e ponto
        const cleaned = value.replace(/[^0-9,.]/g, '');
        setFormData(prev => ({ ...prev, [name]: cleaned }));
    };

    const handleSubmit = async (e) => {
        e.preventDefault();
        
        const obrigatorios = ['codigo_contrato', 'nome', 'status_id'];
        const erros = obrigatorios.filter(campo => !formData[campo] || formData[campo].toString().trim() === '');
        
        if (erros.length > 0) {
            setFaltantes(erros);
            showError("Campos Obrigatrios", "Preencha os campos destacados em vermelho.");
            return;
        }

        // Converte o valor financeiro para o formato do banco (troca vrgula por ponto, se houver, ou limpa)
        let valorFinal = null;
        if (formData.valorContratoCadastroETE) {
            valorFinal = parseFloat(formData.valorContratoCadastroETE.replace(/\./g, '').replace(',', '.'));
            if (isNaN(valorFinal)) valorFinal = null;
        }

        const payload = {
            empreendimento_id: empreendimentoId,
            codigo_contrato: formData.codigo_contrato,
            nome: formData.nome,
            status_id: formData.status_id,
            valorContratoCadastroETE: valorFinal,
            objetoResumidoETE: formData.objetoResumidoETE || ''
        };

        setIsLoading(true);
        try {
            const token = localStorage.getItem('token');
            const res = await fetch(`${API_BASE_URL}/contratos`, {
                method: 'POST',
                headers: {
                    'Content-Type': 'application/json',
                    'Authorization': `Bearer ${token}`
                },
                body: JSON.stringify(payload)
            });

            if (res.ok) {
                showSuccess("Sucesso!", "Novo contrato criado com sucesso!");
                onSaveSuccess();
                onClose();
            } else {
                const data = await res.json();
                showError("Erro", data.error || data.message || "No foi possvel salvar o contrato.");
            }
        } catch (error) {
            console.error("Erro na criao de contrato:", error);
            showError("Erro de Conexo", "No foi possvel conectar ao servidor.");
        } finally {
            setIsLoading(false);
        }
    };

    if (!isOpen) return null;

    return (
        <div className="fixed inset-0 z-[60] flex items-center justify-center bg-black/60 backdrop-blur-sm p-4 animate-fadeIn">
            <div className="bg-white rounded-2xl shadow-2xl w-full max-w-lg overflow-hidden transform transition-all scale-100 relative z-10 flex flex-col max-h-[90vh]">
                
                {/* Header */}
                <div className="bg-slate-50 px-6 py-4 flex justify-between items-center border-b border-gray-100 shrink-0">
                    <div>
                        <h2 className="text-lg font-black text-gray-800 flex items-center gap-2">
                            <span className="text-xl">📄</span> Novo Contrato
                        </h2>
                    </div>
                    <button onClick={onClose} className="text-gray-400 hover:text-red-500 transition-all text-2xl font-light">&times;</button>
                </div>

                {/* Form */}
                <form onSubmit={handleSubmit} className="flex-1 overflow-y-auto p-6 space-y-4 custom-scrollbar">
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                        <div>
                            <label className="block text-xs font-bold text-gray-400 uppercase tracking-wider mb-1.5 ml-1">
                                Código <span className="text-red-500">*</span>
                            </label>
                            <input
                                type="text"
                                name="codigo_contrato"
                                value={formData.codigo_contrato}
                                onChange={handleInputChange}
                                placeholder="Ex: 4600057560"
                                className={`w-full p-3 border-2 rounded-xl bg-gray-50 focus:border-sabesp-blue outline-none font-mono text-sm transition-all ${faltantes.includes('codigo_contrato') ? 'border-red-500 bg-red-50 text-red-700' : 'border-gray-100'}`}
                            />
                        </div>

                        <div>
                            <label className="block text-xs font-bold text-gray-400 uppercase tracking-wider mb-1.5 ml-1">
                                Status da Obra <span className="text-red-500">*</span>
                            </label>
                            <div className="relative">
                                <select
                                    name="status_id"
                                    value={formData.status_id}
                                    onChange={handleInputChange}
                                    className={`w-full p-3 border-2 rounded-xl bg-gray-50 focus:border-sabesp-blue outline-none text-sm font-bold appearance-none cursor-pointer transition-all ${faltantes.includes('status_id') ? 'border-red-500 bg-red-50 text-red-700' : 'border-gray-100'}`}
                                >
                                    <option value="">Selecione...</option>
                                    {statusObras.map(s => (
                                        <option key={s.id} value={s.id}>{s.nome}</option>
                                    ))}
                                </select>
                                <div className="absolute right-3 top-3.5 pointer-events-none text-gray-400 text-xs">▼</div>
                            </div>
                        </div>
                    </div>

                    <div>
                        <label className="block text-xs font-bold text-gray-400 uppercase tracking-wider mb-1.5 ml-1">
                            Nome do Contrato <span className="text-red-500">*</span>
                        </label>
                        <input
                            type="text"
                            name="nome"
                            value={formData.nome}
                            onChange={handleInputChange}
                            placeholder="Ex: ETE Jordanésia - Obras"
                            className={`w-full p-3 border-2 rounded-xl bg-gray-50 focus:border-sabesp-blue outline-none text-sm font-bold transition-all ${faltantes.includes('nome') ? 'border-red-500 bg-red-50 text-red-700' : 'border-gray-100'}`}
                        />
                    </div>

                    <div>
                        <label className="block text-xs font-bold text-gray-400 uppercase tracking-wider mb-1.5 ml-1">
                            Escopo Técnico <span className="lowercase font-normal">(opcional)</span>
                        </label>
                        <textarea
                            name="objetoResumidoETE"
                            value={formData.objetoResumidoETE}
                            onChange={handleInputChange}
                            placeholder="Descreva o objetivo do contrato..."
                            className="w-full p-4 border-2 border-gray-100 rounded-2xl bg-gray-50 h-28 focus:border-sabesp-blue outline-none resize-none text-sm leading-relaxed transition-all"
                        />
                    </div>

                    <div>
                        <label className="block text-xs font-bold text-gray-400 uppercase tracking-wider mb-1.5 ml-1">
                            Valor Total (R$) <span className="lowercase font-normal">(opcional)</span>
                        </label>
                        <div className="relative">
                            <span className="absolute left-4 top-3 text-gray-400 font-bold text-sm">R$</span>
                            <input
                                type="text"
                                name="valorContratoCadastroETE"
                                value={formData.valorContratoCadastroETE}
                                onChange={handleMoneyChange}
                                placeholder="0,00"
                                className="w-full p-3 pl-12 border-2 border-gray-100 rounded-xl bg-gray-50 focus:border-sabesp-blue outline-none font-mono font-bold text-gray-700 transition-all"
                            />
                        </div>
                    </div>

                    {/* Footer / Botões */}
                    <div className="flex justify-end items-center gap-3 pt-4 border-t border-gray-50 shrink-0">
                        <button 
                            type="button" 
                            onClick={onClose}
                            disabled={isLoading}
                            className="px-5 py-2.5 text-gray-400 font-bold hover:text-gray-600 transition-colors text-sm"
                        >
                            Cancelar
                        </button>
                        <button 
                            type="submit"
                            disabled={isLoading}
                            className="px-8 py-2.5 bg-sabesp-blue text-white font-bold rounded-xl hover:bg-[#082855] transition-all shadow-lg hover:shadow-blue-200/50 disabled:opacity-50 active:scale-95 flex items-center justify-center gap-2"
                        >
                            {isLoading ? (
                                <>
                                    <div className="w-4 h-4 border-2 border-white/30 border-t-white rounded-full animate-spin"></div>
                                    <span>Salvando...</span>
                                </>
                            ) : (
                                <span>💾 Salvar Contrato</span>
                            )}
                        </button>
                    </div>
                </form>
            </div>
        </div>
    );
};

export default NovoContratoModal;
